import { accountStore } from "@features/account/account-store"
import { Button } from "@features/atoms/Button"
import { DropdownMenu } from "@features/atoms/DropdownMenu"
import { DropdownItem } from "@features/atoms/menu-hepers"
import { Skeleton } from "@features/atoms/Skeleton"
import { MORE_ICON } from "@features/atoms/svg"
import { useCheckoutConfig } from "@features/tickets/useCheckoutConfig"
import { cn } from "@utils/tw"
import dayjs from "dayjs"
import { Link } from "eddev/routing"
import { useEffect, useState } from "react"
import type { Spektrix } from "../../server/lib/spektrix-types"
import { AccountCarpetLayout, AccountError } from "./Carpet.Account"

type BookedSession = {
  instance: Spektrix.Instance
  event: Spektrix.Event
  tickets: Spektrix.Ticket[]
  orders: BookedOrder[]
}

/** What one order offers for a session. */
type BookedOrder = {
  orderId: string
  /** One barcode per order: a wallet pass holds all of an order's tickets for the session. */
  barcode?: string
  /** E-tickets, downloaded with the customer's Spektrix session. */
  documentIds: string[]
}

export function TicketsCarpetPage() {
  const [orders, setOrders] = useState<Spektrix.Order[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let current = true
    setError(null)
    accountStore.loadOrders().then(
      (orders) => current && setOrders(orders),
      (error: Error) => current && setError(error.message),
    )
    return () => {
      current = false
    }
  }, [attempt])

  const sessions = orders ? bookedSessions(orders) : []
  // Spektrix supplies start in the venue's local time.
  const now = dayjs()
  const upcoming = sessions
    .filter((session) => dayjs(session.instance.start).isAfter(now))
    .sort((a, b) => dayjs(a.instance.start).valueOf() - dayjs(b.instance.start).valueOf())
  const past = sessions
    .filter((session) => !dayjs(session.instance.start).isAfter(now))
    .sort((a, b) => dayjs(b.instance.start).valueOf() - dayjs(a.instance.start).valueOf())

  return (
    <AccountCarpetLayout title="Your tickets">
      {error ? (
        <div className="mt-6 flex flex-col items-start gap-3">
          <AccountError error={error} />
          <Button variant="outline" className="px-5" onClick={() => setAttempt((attempt) => attempt + 1)}>
            Try again
          </Button>
        </div>
      ) : !orders ? (
        <TicketsSkeleton />
      ) : (
        <>
          {upcoming.length ? (
            <SessionList sessions={upcoming} />
          ) : (
            <p className="mt-4 type-body-xs">
              You don't have any upcoming tickets.{" "}
              <Link href="/films" className="underline underline-offset-[3px]">
                See what's playing
              </Link>
            </p>
          )}
          {past.length > 0 && (
            <>
              <h3 className="mt-9 type-label-s">Past</h3>
              <SessionList sessions={past} past />
            </>
          )}
        </>
      )}
    </AccountCarpetLayout>
  )
}

function SessionList({ sessions, past }: { sessions: BookedSession[]; past?: boolean }) {
  return (
    <ul className={cn("mt-4 border-b border-blue", past && "text-blue-light")}>
      {sessions.map((session) => (
        <BookedSessionItem key={session.instance.id} session={session} past={past} />
      ))}
    </ul>
  )
}

function BookedSessionItem({ session, past }: { session: BookedSession; past?: boolean }) {
  const title = session.event.name
  const types = countBy(session.tickets, (ticket) => (ticket.type ?? ticket.ticketType)?.name || "Ticket")
  const seats = session.tickets.map((ticket) => ticket.seatName).filter(Boolean)

  return (
    <li className="border-t border-blue py-4">
      <div className="flex items-start justify-between gap-3">
        <h4 className="type-body-s min-w-0">{title}</h4>
        {!past && !session.instance.cancelled && <TicketMenu title={title} orders={session.orders} />}
      </div>
      <p className="type-label-s mt-1 text-blue-light">
        {dayjs(session.instance.start).format("ddd D MMM YYYY, h:mma")}
        {session.instance.cancelled && " · Cancelled"}
      </p>
      <p className="type-body-xs mt-2">{types.map(([name, count]) => `${name} × ${count}`).join(", ")}</p>
      {seats.length > 0 && <p className="type-body-xs mt-1">Seats {seats.join(", ")}</p>}
    </li>
  )
}

/** E-tickets and Spektrix's Apple and Google Wallet passes, for each order of a session. */
function TicketMenu({ title, orders }: { title: string; orders: BookedOrder[] }) {
  const webApiBaseUrl = useCheckoutConfig().data?.webApiBaseUrl
  if (!webApiBaseUrl) return null
  // The Web API address is `https://<ticketing host>/<client name>/api/`.
  const client = new URL(webApiBaseUrl).pathname.split("/").filter(Boolean)[0]

  const link = (label: string, url: string): DropdownItem => ({
    type: "item",
    label,
    className: "type-body-xs text-blue data-[highlighted]:bg-white-tint",
    onSelect: () => window.open(url, "_blank", "noopener"),
  })
  // A top-level visit to the Web API carries the customer's Spektrix session, which the e-ticket needs.
  const eticketUrl = (id: string) => new URL(`v3/print-at-home-documents/${encodeURIComponent(id)}`, webApiBaseUrl).href
  const passUrl = (wallet: "ApplePass" | "GooglePass", barcode: string) =>
    `https://app.tickets-and-passes.com/api/${wallet}?${new URLSearchParams({ client, barcode })}`

  const groups = orders
    .map((order) => ({
      order,
      actions: [
        ...order.documentIds.map((id, index) =>
          link(order.documentIds.length > 1 ? `Download e-tickets (${index + 1})` : "Download e-tickets", eticketUrl(id)),
        ),
        ...(order.barcode
          ? [
              link("Add to Apple Wallet", passUrl("ApplePass", order.barcode)),
              link("Add to Google Wallet", passUrl("GooglePass", order.barcode)),
            ]
          : []),
      ],
    }))
    .filter((group) => group.actions.length)
  if (!groups.length) return null

  const items = groups.flatMap(({ order, actions }, index): DropdownItem[] => [
    ...(index > 0 ? [{ type: "separator" } as DropdownItem] : []),
    ...(groups.length > 1 ? [{ type: "heading", label: `Order ${order.orderId}` } as DropdownItem] : []),
    ...actions,
  ])

  return (
    <DropdownMenu
      align="end"
      contentClassName="z-[62]"
      trigger={
        <button
          type="button"
          aria-label={`Options for ${title}`}
          className="flex size-[24px] shrink-0 items-center justify-center text-blue-light hover:text-blue outline-none"
        >
          {MORE_ICON}
        </button>
      }
      items={items}
    />
  )
}

function TicketsSkeleton() {
  return (
    <div aria-busy="true">
      <span className="sr-only" role="status">
        Loading your tickets…
      </span>
      <ul className="mt-4 border-b border-blue">
        {[0, 1].map((i) => (
          <li key={i} className="border-t border-blue py-4">
            <Skeleton className="block w-fit type-body-s">The name of a film</Skeleton>
            <Skeleton className="block w-fit type-label-s mt-1">Sat 14 Nov 2026, 7:30pm</Skeleton>
            <Skeleton className="block w-fit type-body-xs mt-2">General Admission × 2</Skeleton>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Gathers every order's tickets by session, since one session can be booked across several orders. */
function bookedSessions(orders: Spektrix.Order[]) {
  const sessions = new Map<string, BookedSession>()
  for (const order of orders) {
    const booked = new Map<string, BookedOrder>()
    for (const ticket of order.tickets ?? []) {
      if (!ticket.instance?.id || !ticket.event) continue
      let session = sessions.get(ticket.instance.id)
      if (!session) {
        session = { instance: ticket.instance, event: ticket.event, tickets: [], orders: [] }
        sessions.set(ticket.instance.id, session)
      }
      session.tickets.push(ticket)
      let entry = booked.get(ticket.instance.id)
      if (!entry) {
        entry = { orderId: order.id, documentIds: [] }
        booked.set(ticket.instance.id, entry)
        session.orders.push(entry)
      }
      entry.barcode ??= ticket.barcode || undefined
    }
    for (const document of order.printAtHomeDocuments ?? []) {
      // Merchandise vouchers have a stock item instead of a session.
      if (!document.id || (document.stockItem && !document.instance)) continue
      for (const [instanceId, entry] of booked) {
        // A document without a session covers the whole order.
        if (!document.instance?.id || document.instance.id === instanceId) entry.documentIds.push(document.id)
      }
    }
  }
  return [...sessions.values()]
}

function countBy<T>(items: T[], key: (item: T) => string) {
  const counts = new Map<string, number>()
  for (const item of items) counts.set(key(item), (counts.get(key(item)) ?? 0) + 1)
  return [...counts]
}
